// mediaAdapter — pure normalizers mapping the app's existing media/storage API
// shapes into the flat item model the Media Library view consumes (WL-023).
//
// The live API groups media by type as `{ url: { size, size_display, path,
// extension, ... } }` (see components/Storage/MediaTable). There is no refcount,
// upload date, or folder on that payload yet — that is the EW-010 / WL-072 gap.
// We normalise honestly: pass through what the BE gives, and leave `usedInWheels`
// as `null` (→ "Usage not tracked yet") until WL-072 ships it, rather than
// fabricating a "0 / unused" that would make deletion look safe when it isn't.
import { formatBytes } from "./helpers";

const EXT_TYPE = {
  jpg: "image", jpeg: "image", png: "image", gif: "image", webp: "image", svg: "image", bmp: "image", heic: "image",
  mp4: "video", mov: "video", webm: "video", m4v: "video", avi: "video", mkv: "video",
  // QA-97 — audiobook / long-form containers classify as audio too, so an
  // uploaded .m4b or .aax lands under Audio in the Media Library instead of
  // falling through to the "document" default.
  mp3: "audio", wav: "audio", ogg: "audio", oga: "audio", opus: "audio", m4a: "audio", aac: "audio", flac: "audio",
  aiff: "audio", aif: "audio", m4b: "audio", aa: "audio", aax: "audio", wma: "audio",
  pdf: "document", doc: "document", docx: "document", ppt: "document", pptx: "document", xls: "document", xlsx: "document", txt: "document",
};

// Map an API group key → item type (image/video/audio/document).
const GROUP_TYPE = { images: "image", image: "image", video: "video", videos: "video", document: "document", documents: "document", audio: "audio", audios: "audio" };

function typeFromExtension(ext) {
  return EXT_TYPE[String(ext || "").toLowerCase().replace(/^\./, "")] || "document";
}

// Normalise ONE `{url, details}` entry from a known group into a flat item.
export function normalizeMediaItem(url, details = {}, group) {
  const name = details.name || String(url || "").split("/").pop() || "untitled";
  const ext = details.extension || (name.includes(".") ? name.split(".").pop() : "");
  const type = GROUP_TYPE[group] || typeFromExtension(ext);
  const size = Number(details.size) || 0;
  return {
    id: details.id ?? url, // stable key; BE id when present, else the url
    url,
    name,
    type,
    extension: ext,
    size,
    sizeDisplay: details.size_display || formatBytes(size),
    uploadedAt: details.created_at || details.uploaded_at || null,
    folder: details.folder_name || details.folder || null,
    duration: details.duration ?? null,
    // EW-010 honesty: only trust an explicit BE refcount; else unknown (null).
    usedInWheels:
      details.used_in_wheels ?? details.usage_count ?? details.wheel_count ?? null,
    // Capture ↔ Media Library unify (Phase 1): BE stamps `source:"capture"` on
    // files under uploads/<id>/capture/ and left-joins CaptureItem for the caption
    // + attached-wheel + captured-at. Non-capture files carry none of these (source
    // stays null), so the Library/picker render is unchanged for normal uploads.
    source: details.source ?? null,
    caption: details.caption ?? null,
    capturedAt: details.captured_at ?? details.capturedAt ?? null,
    attachedWheel: details.attached_wheel ?? details.attachedWheel ?? null,
    captureType: details.capture_type ?? null,
  };
}

// Flatten the grouped media payload (`response.data.data.data.data`, or any
// `{group: {url: details}}` map) into a single ordered item array.
export function normalizeMediaResponse(grouped) {
  if (!grouped || typeof grouped !== "object") return [];
  const out = [];
  for (const [group, entries] of Object.entries(grouped)) {
    if (!entries || typeof entries !== "object") continue;
    for (const [url, details] of Object.entries(entries)) {
      out.push(normalizeMediaItem(url, details, group));
    }
  }
  return out;
}

// ── EW-1280: real live api/media/ shape ───────────────────────────────────────
// The LIVE `GET api/media/` payload is NOT the `{group:{url:details}}` map the
// legacy normalizer above assumes. It is:
//   res.data.data.types = { images: { data: [ {file}, … ] }, videos: {data:[…]}, … }
// where each {file} = { size:"294.85 KB", name, path, url, created_at, metadata }.
// Two differences from the legacy map: (1) files live in an ARRAY under `.data`,
// keyed by category, and (2) `size` is a human DISPLAY STRING, not a byte number.
// The grid renders `formatBytes(item.size)`, so `size` MUST be numeric bytes here.

const UNIT_BYTES = {
  b: 1, byte: 1, bytes: 1,
  kb: 1024, mb: 1024 ** 2, gb: 1024 ** 3, tb: 1024 ** 4, pb: 1024 ** 5,
};

// Parse a size that may be a number (bytes) OR a display string ("294.85 KB",
// "80.19 MB", "639 bytes") into a byte count. Anything unparseable → 0 (never NaN,
// so sort-by-size and formatBytes stay well-defined).
export function parseSizeToBytes(size) {
  if (typeof size === "number") return Number.isFinite(size) && size > 0 ? size : 0;
  if (typeof size !== "string") return 0;
  const m = size.trim().match(/^([\d.]+)\s*([a-zA-Z]*)$/);
  if (!m) return 0;
  const value = parseFloat(m[1]);
  if (!Number.isFinite(value) || value < 0) return 0;
  const unit = (m[2] || "b").toLowerCase();
  const mult = UNIT_BYTES[unit];
  if (!mult) return 0;
  return Math.round(value * mult);
}

// Normalise ONE real file object from `types.{category}.data[]` into a flat item.
export function normalizeMediaFile(file = {}, category) {
  const url = file.url || file.path || "";
  const name = file.name || String(url).split("/").pop() || "untitled";
  const ext = file.extension || (name.includes(".") ? name.split(".").pop() : "");
  const type = GROUP_TYPE[category] || typeFromExtension(ext);
  const size = parseSizeToBytes(file.size);
  return {
    // Stable key: BE id when present, else the (unique) storage path, else url/name.
    id: file.id ?? file.path ?? url ?? name,
    // Storage path the delete endpoint (api/media/delete/) keys on (EW-1281). Carried
    // explicitly so deletion never has to guess it back out of `id`/`url`.
    path: file.path ?? null,
    url,
    // Real asset URL for image thumbnails; non-images fall back to the type glyph.
    thumbnail: file.thumbnail || (type === "image" ? url : null),
    name,
    type,
    extension: ext,
    size,
    // Prefer the BE's own display string when it gave one; else derive from bytes.
    sizeDisplay: typeof file.size === "string" ? file.size : formatBytes(size),
    uploadedAt: file.created_at || file.uploaded_at || null,
    folder: file.folder_name || file.folder || null,
    // gordon-1005-P1-media-library-folders-build: BE PR#3701 attributes each
    // file to its folder (if any) by url-matching a WheelRecentMedia anchor
    // row -- folderId drives "filter by folder" / which-folder-am-I-in;
    // mediaId is that anchor row's own id (null when unfiled).
    folderId: file.folder_id ?? null,
    mediaId: file.media_id ?? null,
    duration: file.duration ?? null,
    // EW-010 honesty: only trust an explicit BE refcount; else unknown (null).
    usedInWheels:
      file.used_in_wheels ?? file.usage_count ?? file.wheel_count ?? null,
    // Capture ↔ Media Library unify (Phase 1): carry the BE's prefix-tag + joined
    // CaptureItem metadata through to the flat item the grid/picker/filter consume.
    // Non-capture files leave `source` null → identical render to today.
    source: file.source ?? null,
    caption: file.caption ?? null,
    capturedAt: file.captured_at ?? file.capturedAt ?? null,
    attachedWheel: file.attached_wheel ?? file.attachedWheel ?? null,
    captureType: file.capture_type ?? null,
  };
}

// Flatten the live `types` map (`{category: {data: [file, …]}}`, or a defensive
// `{category: [file, …]}`) into a single ordered item array. Returns [] when there
// is genuinely no media, so the page's empty state becomes reachable (EW-1280).
export function normalizeMediaTypes(types) {
  if (!types || typeof types !== "object") return [];
  const out = [];
  for (const [category, bucket] of Object.entries(types)) {
    const files = Array.isArray(bucket)
      ? bucket
      : bucket && Array.isArray(bucket.data)
        ? bucket.data
        : [];
    for (const file of files) {
      if (file && typeof file === "object") out.push(normalizeMediaFile(file, category));
    }
  }
  return out;
}

// gordon-1005-P1-media-library-folders-build: split the BE's unified
// folders+files `items` list (users/views.py build_media_library_items,
// flag-gated MEDIA_LIBRARY_FOLDER_JOIN) into the two flat shapes the page
// renders -- real folders (with a count) instead of the permanent `[]` stub,
// and files normalised the same way normalizeMediaTypes already does.
export function splitMediaLibraryItems(items) {
  const folders = [];
  const files = [];
  for (const it of items || []) {
    if (!it) continue;
    if (it.type === "folder") {
      folders.push({ id: it.id, name: it.name, count: it.media_count ?? 0 });
    } else {
      files.push(normalizeMediaFile(it));
    }
  }
  return { folders, files };
}

// Normalise the total-storage summary → strip props for the header.
export function normalizeStorage(summary = {}) {
  const percentRaw =
    summary.storage_used_percentage ?? summary.storage_used_percent ?? 0;
  const percent =
    typeof percentRaw === "string" ? parseFloat(percentRaw.replace("%", "")) : Number(percentRaw);
  // The RAW byte count, carried alongside the label. The label is a ROUNDED
  // display string ("250 MB"), and rounding it back to bytes destroys any
  // overage smaller than the rounding step -- measured on Development
  // 2026-09-09, an account 2,128 bytes past its 250 MB cap reported
  // storage_used "250 MB", which parses to exactly the cap, so the storage
  // bar read "not over" while Settings/Account read "100.0% - over your
  // limit" for the same account in the same minute. null (not 0) when the
  // server omits it: 0 would be a claim of emptiness, null is "unknown" and
  // lets the bar fall back to the label.
  const usedBytesRaw = Number(summary.storage_used_bytes);
  return {
    usedLabel: summary.storage_used || "0 MB",
    usedBytes: Number.isFinite(usedBytesRaw) ? usedBytesRaw : null,
    totalLabel: summary.storage_limit || "5 GB",
    percent: Number.isFinite(percent) ? percent : 0,
    exceeded: Boolean(summary.storage_limit_exceeded),
  };
}
